import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  RulesEngineFormValues,
  TriggerSource,
} from '../rules-engine-form-page-old';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { Folder, SharedDriveModel } from '../../../../../types';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { UserKeys } from '../../../../api/users-api';
import UsersAutocomplete from '../../../../shared/users-autocomplete';
import GroupsAutocomplete from '../../../../shared/groups-autocomplete';
import DomainsAutoComplete from '../../../../shared/domain-autocomplete';
import OrganizationsAutoComplete from '../../../../shared/organizations-autocomplete';
import SharedDrivesAutocomplete from '../../../../shared/shared-drives-autocomplete';
import SharedDrivesBigQueryAutocomplete from '../../../../shared/shared-drive-bigquery-autocomplete';
import AvatarChip from '../../../../shared/avatar-chip';
import DriveTreeDialog from './drive-tree-dialog';
import { SectionHeader } from '../../../../shared/section-header';
import {
  triggerEventOptions,
  triggerGroupUiOptions,
  getEventUiGroup,
} from '../trigger-event-options';
import { RuleModel } from '../../../../../types';
import AlertList from '../../../../shared/alert-list';
import StatusLabel, { StatusType } from '../../../../shared/status-label';

import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

interface RulesEngineBasicInformationStepProps {
  control: Control<RulesEngineFormValues>;
  errors: FieldErrors<RulesEngineFormValues>;
  watch: UseFormWatch<RulesEngineFormValues>;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  sourceType: 'User' | 'Group' | 'Orgs' | 'Domain';
  setSourceType: Dispatch<SetStateAction<'User' | 'Group' | 'Domain' | 'Orgs'>>;
  getValues: UseFormGetValues<RulesEngineFormValues>;
  trigger: UseFormTrigger<RulesEngineFormValues>;
  domains: string[];
  isCalendarResourceEvent: boolean;
  setSelectedRule: (rule: RuleModel) => void;
  allRules: RuleModel[];
}

export default function RulesEngineBasicInformationStep({
  control,
  errors,
  watch,
  setValue,
  getValues,
  trigger,
  sourceType,
  setSourceType,
  domains,
  isCalendarResourceEvent,
  setSelectedRule,
  allRules,
}: RulesEngineBasicInformationStepProps) {
  const triggerGroupOptions = triggerGroupUiOptions;

  const [triggerList, setTriggerList] = useState([
    'User',
    'Group',
    'Orgs',
    'Domain',
  ]);

  const entityList = ['User', 'Group', 'Orgs', 'Domain'];
  const durationTypeOptions = [
    { value: 'minutes', displayValue: 'Minutes' },
    { value: 'hours', displayValue: 'Hours' },
    { value: 'days', displayValue: 'Days' },
  ];

  const occursEverytime = watch('triggerInfo.occursEverytime');
  const triggerSources = watch('triggerSources');
  const triggerGroup = watch('triggerInfo.triggerGroup');
  const triggerEvent = watch('triggerInfo.triggerEvent');
  const allSource = watch('allSource');
  const targetsExcluded = watch('targetsExcluded');

  const selectedTriggerGroup = getEventUiGroup(triggerEvent);

  const [regex, setRegex] = useState('');
  const queryClient = useQueryClient();
  const [hasRegexError, setHasRegexError] = useState(false);
  const [sharedDrivesAutoCompleteValue, setSharedDrivesAutoCompleteValue] =
    useState('');
  const [userDriveAutoCompleteValue, setUserDriveAutoCompleteValue] =
    useState('');
  const [openDriveTreeDialog, setOpenDriveTreeDialog] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState('');
  const { currentTier } = useUserSettings();
  const [selectedDrive, setSelectedDrive] = useState<SharedDriveModel | null>(
    null
  );

  const currentTriggerEventOption =
    triggerEventOptions.find(
      (event) => event.value === watch('triggerInfo.triggerEvent')
    ) || triggerEventOptions[0];

  const currentRuleId = watch('id');
  const similarRules = useMemo(() => {
    if (!allRules || !triggerGroup || !triggerEvent) {
      return [];
    }
    return allRules.filter(
      (rule) =>
        rule.id !== currentRuleId &&
        rule.triggerInfo.triggerGroup === triggerGroup &&
        rule.triggerInfo.triggerEvent === triggerEvent
    );
  }, [allRules, triggerGroup, triggerEvent, currentRuleId]);

  const triggerEventDisplay =
    triggerEventOptions.find((o) => o.value === triggerEvent)?.displayValue ||
    '';

  const indexOfCurrentEntity = entityList.indexOf(
    currentTriggerEventOption.lowestEntityAllowed
  );
  const allowedEntityList = entityList.slice(indexOfCurrentEntity);

  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const autocompleteKey = useRef(new Date().getTime().toString());
  const [autoCompleteExcludeResponse, setAutoCompleteExcludeResponse] =
    useState('');
  const autocompleteExcludeKey = useRef(
    `${new Date().getTime().toString()}-exclude`
  );

  const [driveEntityType, setDriveEntityType] = useState<
    'Shared Drives' | 'User Drive' | 'Regular Expressions'
  >('Shared Drives');

  const { fields, remove } = useFieldArray({
    control,
    name: 'triggerSources',
  });

  const handleAddAllUsersChecked = (value: boolean) => {
    setValue('allSource', value);

    setValue(
      'triggerSources',
      fields.filter((source) => source.type !== 'User')
    );
    setValue('targetsExcluded', []);
    setSourceType('User');
  };

  useEffect(() => {
    if (allSource) {
      setSourceType('User');
    }
  }, [allSource, setSourceType]);

  const handleRemoveExcludedUser = (user: string) => {
    const currentExcluded = getValues('targetsExcluded') ?? [];
    setValue(
      'targetsExcluded',
      currentExcluded.filter((excludedUser) => excludedUser !== user)
    );
  };

  function addSelection(source: TriggerSource) {
    setValue('triggerSources', [...fields, source]);

    if (fields.length === 0) {
      trigger('triggerSources');
    }
    autocompleteKey.current = new Date().getTime().toString();
    setAutoCompleteResponse('');
    setSharedDrivesAutoCompleteValue('');
    setRegex('');
    setOpenDriveTreeDialog(false);
    setUserDriveAutoCompleteValue('');
  }

  const getDriveFolderName = useCallback(
    (userEmail: string, folderId: string) => {
      const allFolders = queryClient.getQueryData<
        InfiniteData<Folder[], unknown> | undefined
      >([UserKeys.DriveFolders, userEmail]);

      const folders = allFolders?.pages.reduce(
        (acc, page) => acc.concat(page),
        []
      );

      const folder = folders?.find((folder) => folder.id === folderId);

      return folder?.title || '';
    },
    [queryClient]
  );

  function checkIfExisting(value: string, type: string) {
    if (
      !value ||
      triggerSources?.some((src) => src.value === value && src.type === type)
    ) {
      return true;
    }
  }

  function resetDriveFilters() {
    setValue('hasFileTypeFilter', false);
    setValue('hasFileSizeFilter', false);
    setValue('triggerInfo.fileSharedToSpecificUser', []);
    setValue('hasSelectedDomainSharing', false);
    setValue('sizeComparison', 'larger');
    setValue('triggerInfo.canFindInSearchResults', false);
    setValue('triggerInfo.fileMimeTypes', []);
    setValue('triggerInfo.driveFileSizeCeiling', 0);
    setValue('triggerInfo.driveFileSizeFloor', 0);
    setValue('sizeType', undefined);
    setValue('hasSharingPermissionsFilter', false);
    setValue('selectedSharingOption', undefined);
    setValue('triggerInfo.fileIsSharedToDomain', false);
    setValue('triggerInfo.fileIsSharedPublic', false);
    setValue('triggerInfo.fileSharedToUserExternalToDomain', false);
    setValue('triggerInfo.fileSharedToUserInternalToDomain', false);
    setValue('triggerInfo.mustHaveLinkToAccess', false);
    setValue('hasSharingPermissionsFilter', false);
  }

  function resetAdminFilters() {
    setValue('hasProfileFieldFilter', false);
    setValue('profileFieldFilter.profileFieldType', undefined);
    setValue('profileFieldFilter.profileFieldValue', undefined);
    setValue('profileFieldFilter.profileFieldRegex', undefined);
    setValue('profileFieldFilter.profileFieldValueExact', false);
    setValue('groupInfo.groupEmail', []);
    setValue('groupInfo.groupRole', []);
  }

  return (
    <Stack width={'100%'} gap={2}>
      <Stack gap={1}>
        <SectionHeader
          description=" Please provide the name of the rule you are creating and select the
        trigger group and event from the dropdowns below."
        >
          Trigger Information
        </SectionHeader>
        <Grid container columnSpacing={2} rowSpacing={1.5} size={{ xs: 10 }}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="basicInfo.ruleName"
              control={control}
              rules={{
                required: 'Name is required.',
              }}
              render={({ field }) => (
                <TextField
                  autoFocus
                  required
                  inputProps={{
                    id: 'ruleName',
                  }}
                  InputLabelProps={{
                    htmlFor: 'ruleName',
                  }}
                  label="Name"
                  error={!!errors.basicInfo?.ruleName}
                  helperText={errors?.basicInfo?.ruleName?.message || ' '}
                  {...field}
                  sx={{ width: '100%' }}
                  autoComplete="off"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="basicInfo.description"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'description',
                  }}
                  InputLabelProps={{
                    htmlFor: 'description',
                  }}
                  label="Description"
                  {...field}
                  sx={{ width: '100%' }}
                  autoComplete="off"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="triggerInfo.triggerGroup"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'triggerGroup',
                  }}
                  InputLabelProps={{
                    htmlFor: 'triggerGroup',
                  }}
                  label="Trigger Group"
                  select
                  {...field}
                  value={selectedTriggerGroup}
                  onChange={(e) => {
                    const uiGroup = e.target.value;
                    const groupOption = triggerGroupOptions.find(
                      (option) => option.value === uiGroup
                    )!;
                    // Persist the backend application name, not the UI group.
                    field.onChange(groupOption.appName);
                    resetDriveFilters();
                    resetAdminFilters();
                    setValue('actionInfo.actionTodo', []);

                    // Set the allowable trigger events based on the selected trigger group
                    const updatedTriggerEvent = triggerEventOptions.find(
                      (option) => getEventUiGroup(option.value) === uiGroup
                    )!.value;
                    setValue('triggerInfo.triggerEvent', updatedTriggerEvent);

                    // Reset the trigger sources when the trigger group changes
                    setValue('triggerSources', []);
                    setValue('triggerInfo.triggerUserSources', []);
                    setValue('triggerInfo.triggerGroupSources', []);
                    setValue('triggerInfo.triggerOrgSources', []);
                    setValue('triggerInfo.triggerDomainSources', []);
                    setValue('triggerInfo.sharedDriveIds', []);
                    setValue('allSource', false);
                    setValue('targetsExcluded', []);
                    setValue('triggerInfo.driveRegExs', []);
                    setValue('triggerInfo.driveFolderTriggerSources', []);
                    setValue('triggerInfo.triggerCalendarSources', []);

                    // Set the source type to the lowest entity allowed for the selected trigger group
                    const updatedSourceType = triggerEventOptions.find(
                      (option) => getEventUiGroup(option.value) === uiGroup
                    )!.lowestEntityAllowed as
                      | 'User'
                      | 'Group'
                      | 'Orgs'
                      | 'Domain';
                    setSourceType(updatedSourceType);

                    // if the trigger group is drive, add shared drive to the allowed entity list
                    if (
                      uiGroup === 'drive' &&
                      !triggerList.includes('Shared Drive')
                    ) {
                      setTriggerList((prev) => [...prev, 'Shared Drive']);
                    } else {
                      setTriggerList((prev) =>
                        prev.filter((entity) => entity !== 'Shared Drive')
                      );
                    }

                    // if the trigger group is drive, add regex to the allowed entity list
                    if (uiGroup === 'drive' && !triggerList.includes('RegEx')) {
                      setTriggerList((prev) => [...prev, 'RegEx']);
                    } else {
                      setTriggerList((prev) =>
                        prev.filter((entity) => entity !== 'RegEx')
                      );
                    }
                  }}
                  sx={{ width: '100%' }}
                  autoComplete="off"
                >
                  {triggerGroupOptions.map((option) => (
                    <MenuItem value={option.value} key={option.displayValue}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="triggerInfo.triggerEvent"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'triggerEvent',
                  }}
                  InputLabelProps={{
                    htmlFor: 'triggerEvent',
                  }}
                  label="Trigger Event"
                  select
                  {...field}
                  onChange={(e) => {
                    field.onChange(e.target.value);

                    // Reset the trigger sources when the trigger event changes
                    setValue('triggerSources', []);
                    setValue('triggerInfo.triggerUserSources', []);
                    setValue('triggerInfo.triggerGroupSources', []);
                    setValue('triggerInfo.triggerOrgSources', []);
                    setValue('triggerInfo.triggerDomainSources', []);
                    setValue('triggerInfo.sharedDriveIds', []);
                    setValue('allSource', false);
                    setValue('targetsExcluded', []);
                    setValue('triggerInfo.driveRegExs', []);
                    setValue('triggerInfo.driveFolderTriggerSources', []);
                    setValue('triggerInfo.triggerCalendarSources', []);

                    // Set the source type to the lowest entity allowed for the selected trigger event
                    const updatedSourceType = triggerEventOptions.find(
                      (option) => option.value === e.target.value
                    )!.lowestEntityAllowed as
                      | 'User'
                      | 'Group'
                      | 'Orgs'
                      | 'Domain';
                    setSourceType(updatedSourceType);
                  }}
                  sx={{ width: '100%' }}
                  autoComplete="off"
                >
                  {triggerEventOptions
                    .filter(
                      (option) =>
                        getEventUiGroup(option.value) === selectedTriggerGroup
                    )
                    .map((option) => (
                      <MenuItem value={option.value} key={option.displayValue}>
                        {option.displayValue}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            {similarRules.length > 0 && (
              <AlertList
                severity="info"
                items={similarRules}
                itemsThresholdBeforeScroll={4}
                message={
                  <Typography>
                    We found {similarRules.length}{' '}
                    {similarRules.length === 1
                      ? 'similar rule'
                      : 'similar rules'}{' '}
                    already targeting '
                    {triggerGroupOptions.find(
                      (o) => o.value === selectedTriggerGroup
                    )?.displayValue || triggerGroup}{' '}
                    → {triggerEventDisplay}'
                  </Typography>
                }
                renderItem={(rule: RuleModel) => {
                  const status =
                    (rule.basicInfo.status?.toUpperCase() as StatusType) ??
                    null;
                  const statusChip = status ? (
                    <StatusLabel status={status} />
                  ) : null;
                  return (
                    <Box
                      key={rule.id}
                      sx={{
                        p: 1,
                        width: '100%',
                        backgroundColor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          fontWeight="bold"
                          noWrap
                          sx={{ flexGrow: 1, textOverflow: 'ellipsis' }}
                        >
                          {rule.basicInfo.ruleName}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          sx={{ flexShrink: 0 }}
                        >
                          {statusChip}
                          <Link
                            component="button"
                            variant="body2"
                            type="button"
                            onClick={() => {
                              setSelectedRule(rule);
                            }}
                          >
                            View Rule
                          </Link>
                        </Stack>
                      </Stack>
                    </Box>
                  );
                }}
              />
            )}
          </Grid>
        </Grid>
      </Stack>
      <Stack gap={1}>
        <SectionHeader description="Rules can be triggered based on the number of times an event occurs within an optional time duration, or every time an event occurs.">
          Occurrences
        </SectionHeader>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <InfoOutlined fontSize="small" color="info" />
          <Typography variant="caption">
            Occurrence settings are not reflected in the Preview Impact
            simulation.
          </Typography>
        </Stack>
        <Grid container columnSpacing={2} size={{ xs: 10 }}>
          <Grid size={{ xs: 12 }} sx={{ pl: 2 }}>
            <Controller
              name="triggerInfo.occursEverytime"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  value={field.value ? 'everyTime' : 'threshold'}
                  onChange={(e) => {
                    const everyTime = e.target.value === 'everyTime';
                    field.onChange(everyTime);
                    if (everyTime && getValues('triggerInfo.hasTimeDuration')) {
                      setValue('triggerInfo.hasTimeDuration', false);
                    }
                  }}
                  sx={{ gap: 1 }}
                >
                  <FormControlLabel
                    value="everyTime"
                    control={<Radio inputProps={{ id: 'occursEverytime' }} />}
                    label="Every time the event occurs"
                  />
                  <Stack flexDirection="row" alignItems="center" gap={2}>
                    <FormControlLabel
                      value="threshold"
                      control={<Radio />}
                      label="When an event occurs"
                      sx={{ mr: 0 }}
                    />
                    <Box sx={{ width: 90 }}>
                      <Controller
                        name="triggerInfo.triggerThreshold"
                        control={control}
                        rules={{
                          validate: (value) => occursEverytime || value! > 0,
                        }}
                        render={({ field: thresholdField }) => (
                          <TextField
                            inputProps={{
                              id: 'triggerThreshold',
                              min: 1,
                            }}
                            type="number"
                            label="Threshold"
                            {...thresholdField}
                            autoComplete="off"
                            error={!!errors.triggerInfo?.triggerThreshold}
                            disabled={occursEverytime}
                            onChange={(e) =>
                              thresholdField.onChange(
                                e.target.value
                                  ? Number(e.target.value)
                                  : e.target.value
                              )
                            }
                          />
                        )}
                      />
                    </Box>
                    <Typography>times</Typography>
                  </Stack>
                  <Box sx={{ pl: 4 }}>
                    <Stack flexDirection="row" gap={2} alignItems="center">
                      <Controller
                        name="triggerInfo.hasTimeDuration"
                        control={control}
                        render={({ field: durationToggle }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                inputProps={{
                                  id: 'hasTimeDuration',
                                }}
                                {...durationToggle}
                                checked={durationToggle.value}
                                onChange={(e) =>
                                  durationToggle.onChange(e.target.checked)
                                }
                                disabled={occursEverytime}
                              />
                            }
                            label="Within a time window of"
                            sx={{ mr: 0 }}
                          />
                        )}
                      />
                      <Box sx={{ width: 80 }}>
                        <Controller
                          name="triggerInfo.triggerDuration"
                          control={control}
                          rules={{
                            validate: (value) =>
                              occursEverytime ||
                              !watch('triggerInfo.hasTimeDuration') ||
                              value! > 0,
                          }}
                          render={({ field: durationField }) => (
                            <TextField
                              inputProps={{
                                id: 'duration',
                                min: 1,
                              }}
                              type="number"
                              label="Duration"
                              {...durationField}
                              autoComplete="off"
                              error={!!errors.triggerInfo?.triggerDuration}
                              disabled={
                                occursEverytime ||
                                !watch('triggerInfo.hasTimeDuration')
                              }
                              onChange={(e) =>
                                durationField.onChange(
                                  e.target.value
                                    ? Number(e.target.value)
                                    : e.target.value
                                )
                              }
                            />
                          )}
                        />
                      </Box>
                      <Controller
                        name="triggerInfo.triggerDurationType"
                        control={control}
                        render={({ field: durationTypeField }) => (
                          <TextField
                            inputProps={{
                              id: 'durationType',
                            }}
                            InputLabelProps={{
                              htmlFor: 'durationType',
                            }}
                            label="Type"
                            select
                            {...durationTypeField}
                            sx={{ width: 100 }}
                            autoComplete="off"
                            disabled={
                              occursEverytime ||
                              !watch('triggerInfo.hasTimeDuration')
                            }
                          >
                            {durationTypeOptions.map((option) => (
                              <MenuItem
                                value={option.value}
                                key={option.displayValue}
                              >
                                {option.displayValue}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Stack>
                  </Box>
                </RadioGroup>
              )}
            />
          </Grid>
        </Grid>
      </Stack>
      {!isCalendarResourceEvent && (
        <>
          <SectionHeader description="Trigger sources like users, groups, orgs, or domains act as filters for when the rule should be triggered. Drive events can also be filtered by a shared drive, a specific user's drive folder, or regular expressions.">
            Trigger Sources
          </SectionHeader>
          <Grid container size={{ xs: 10 }}>
            <Grid
              container
              columnSpacing={2}
              rowSpacing={1.5}
              size={{ xs: 12 }}
            >
              <Grid size={{ xs: 12, lg: 6 }}>
                <Stack flexDirection="row" justifyContent="space-between">
                  <Stack gap={2} sx={{ width: '100%' }}>
                    <FormControl>
                      <FormLabel
                        sx={{
                          fontWeight: 'bold',
                          color: (theme) => theme.palette.text.primary,
                        }}
                      >
                        Sources
                      </FormLabel>
                      <RadioGroup
                        aria-labelledby="radio-buttons-group-label"
                        defaultValue="User"
                        name="radio-buttons-group"
                        value={sourceType}
                        onChange={(event) => {
                          const newSourceType = event?.target.value as
                            | 'User'
                            | 'Group'
                            | 'Orgs'
                            | 'Domain';
                          setSourceType(newSourceType);

                          // If the new source type is not 'User', reset the 'allSource' flag
                          if (newSourceType !== 'User') {
                            setValue('allSource', false);
                            setValue('targetsExcluded', []);
                          }
                        }}
                        row
                      >
                        {allowedEntityList.map((entity) => (
                          <FormControlLabel
                            key={entity}
                            value={entity}
                            control={<Radio />}
                            label={entity}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <Stack gap={1} flexDirection="row" sx={{ width: '100%' }}>
                      <Stack
                        flexDirection={'row'}
                        gap={1}
                        sx={{ width: '100%' }}
                      >
                        <Controller
                          name={'triggerSources'}
                          control={control}
                          render={() => (
                            <Box sx={{ width: '100%' }}>
                              {sourceType === 'User' ? (
                                <UsersAutocomplete
                                  newKey={`${autocompleteKey.current}user`}
                                  autocompleteValue={autoCompleteResponse}
                                  setAutoCompleteValue={(value) => {
                                    if (checkIfExisting(value, sourceType)) {
                                      return;
                                    }
                                    setAutoCompleteResponse(value);
                                    addSelection({
                                      type: sourceType,
                                      value: value,
                                    });
                                  }}
                                  errors={!!errors.triggerSources}
                                  helperText={
                                    errors?.triggerSources?.message || undefined
                                  }
                                  disabled={allSource}
                                />
                              ) : sourceType === 'Group' ? (
                                <GroupsAutocomplete
                                  newKey={`${autocompleteKey.current}group`}
                                  autocompleteValue={autoCompleteResponse}
                                  setAutoCompleteValue={(value) => {
                                    if (checkIfExisting(value, sourceType)) {
                                      return;
                                    }
                                    setAutoCompleteResponse(value);
                                    addSelection({
                                      type: sourceType,
                                      value: value,
                                    });
                                  }}
                                  errors={!!errors.triggerSources}
                                  helperText={
                                    errors?.triggerSources?.message || undefined
                                  }
                                />
                              ) : sourceType === 'Domain' ? (
                                <DomainsAutoComplete
                                  newKey={`${autocompleteKey.current}domain`}
                                  size="medium"
                                  domains={domains}
                                  setAutoCompleteValue={(value) => {
                                    if (checkIfExisting(value, sourceType)) {
                                      return;
                                    }
                                    setAutoCompleteResponse(value);
                                    addSelection({
                                      type: sourceType,
                                      value: value,
                                    });
                                  }}
                                  hasError={!!errors.triggerSources}
                                  helperText={
                                    errors?.triggerSources?.message || undefined
                                  }
                                />
                              ) : (
                                <OrganizationsAutoComplete
                                  newKey={`${autocompleteKey.current}org`}
                                  size="medium"
                                  autoCompleteValue={autoCompleteResponse}
                                  setAutoCompleteValue={(value) => {
                                    if (checkIfExisting(value, sourceType)) {
                                      return;
                                    }
                                    setAutoCompleteResponse(value);
                                    addSelection({
                                      type: sourceType,
                                      value: value,
                                    });
                                  }}
                                  hasError={!!errors.triggerSources}
                                  helperText={
                                    errors?.triggerSources?.message || undefined
                                  }
                                />
                              )}
                            </Box>
                          )}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </Grid>
              {currentTriggerEventOption.type === 'drive' ? (
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Stack gap={2}>
                    <FormControl>
                      <FormLabel
                        sx={{
                          fontWeight: 'bold',
                          color: (theme) => theme.palette.text.primary,
                        }}
                      >
                        Drive Sources
                      </FormLabel>
                      <RadioGroup
                        aria-labelledby="drive-entities-label"
                        defaultValue="User"
                        name="drive-entities"
                        value={driveEntityType}
                        onChange={(event) => {
                          setDriveEntityType(
                            event?.target.value as
                              | 'Shared Drives'
                              | 'Regular Expressions'
                          );
                        }}
                        row
                      >
                        <FormControlLabel
                          key={'Shared Drives'}
                          value={'Shared Drives'}
                          control={<Radio />}
                          label={'Shared Drives'}
                        />
                        <FormControlLabel
                          key={'User Drive'}
                          value={'User Drive'}
                          control={<Radio />}
                          label={'User Drive'}
                        />
                        <FormControlLabel
                          key={'Regular Expressions'}
                          value={'Regular Expressions'}
                          control={<Radio />}
                          label={'Regex'}
                        />
                      </RadioGroup>
                    </FormControl>
                    <Stack gap={1} flexDirection="row">
                      <Box width={'100%'}>
                        {driveEntityType === 'Shared Drives' ? (
                          currentTier !== 'enterprise' ? (
                            <Tooltip
                              arrow
                              title={
                                "This searches for a shared drive whose title contains the word you are typing in. Ex: name contains 'confidential' when typing confidential"
                              }
                            >
                              <SharedDrivesAutocomplete
                                newKey={`${autocompleteKey.current}`}
                                size="medium"
                                autocompleteValue={
                                  sharedDrivesAutoCompleteValue
                                }
                                onDriveSelect={(drive) => {
                                  setSelectedDrive(drive);
                                  if (
                                    selectedDrive &&
                                    !checkIfExisting(
                                      selectedDrive.name,
                                      'Shared Drive'
                                    )
                                  ) {
                                    addSelection({
                                      type: 'Shared Drive',
                                      value: selectedDrive.name,
                                      selectedSharedDriveId: selectedDrive.id,
                                    });
                                  }
                                }}
                                setAutoCompleteValue={
                                  setSharedDrivesAutoCompleteValue
                                }
                                errors={!!errors.triggerSources}
                                helperText={
                                  errors?.triggerSources?.root?.message ||
                                  undefined
                                }
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip
                              arrow
                              title={
                                "This searches for a shared drive whose title contains the word you are typing in. Ex: name contains 'confidential' when typing confidential"
                              }
                            >
                              <SharedDrivesBigQueryAutocomplete
                                newKey={`${autocompleteKey.current}sharedDrive`}
                                size="medium"
                                autocompleteValue={
                                  sharedDrivesAutoCompleteValue
                                }
                                onDriveSelect={(drive) => {
                                  setSelectedDrive(drive);
                                  if (
                                    drive &&
                                    !checkIfExisting(drive.name, 'Shared Drive')
                                  ) {
                                    addSelection({
                                      type: 'Shared Drive',
                                      value: drive.name,
                                      selectedSharedDriveId: drive.id,
                                    });
                                  }
                                }}
                                setAutoCompleteValue={
                                  setSharedDrivesAutoCompleteValue
                                }
                                errors={!!errors.triggerSources}
                                helperText={
                                  errors?.triggerSources?.root?.message ||
                                  undefined
                                }
                              />
                            </Tooltip>
                          )
                        ) : driveEntityType === 'Regular Expressions' ? (
                          <TextField
                            fullWidth
                            inputProps={{
                              id: 'regex',
                            }}
                            InputLabelProps={{
                              htmlFor: 'regex',
                            }}
                            label="Regular Expression"
                            autoComplete="off"
                            value={regex}
                            onChange={(e) => {
                              setRegex(e.target.value);
                              try {
                                new RegExp(e.target.value);
                                setHasRegexError(false);
                              } catch {
                                setHasRegexError(true);
                              }
                            }}
                            error={hasRegexError}
                            helperText={
                              hasRegexError
                                ? 'Invalid regular expression.'
                                : undefined
                            }
                            placeholder="^[a-zA-Z0-9_]+\.txt$"
                          />
                        ) : (
                          <UsersAutocomplete
                            newKey={`${autocompleteKey.current}user`}
                            autocompleteValue={userDriveAutoCompleteValue}
                            setAutoCompleteValue={(value) => {
                              setUserDriveAutoCompleteValue(value);
                              setOpenDriveTreeDialog(true);
                            }}
                            errors={!!errors.triggerSources}
                            helperText={
                              errors?.triggerSources?.root?.message || undefined
                            }
                          />
                        )}
                      </Box>
                      {driveEntityType === 'Regular Expressions' ? (
                        <Button
                          sx={{ height: 40 }}
                          variant="contained"
                          onClick={() => {
                            if (!hasRegexError) {
                              addSelection({
                                type: 'RegEx',
                                value: regex,
                              });
                              setHasRegexError(false);
                            } else {
                              setHasRegexError(true);
                            }
                          }}
                          disabled={
                            (driveEntityType === 'Regular Expressions' &&
                              regex.length === 0) ||
                            hasRegexError
                          }
                        >
                          Add
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>
                </Grid>
              ) : null}
              <Grid size={{ xs: 12 }}>
                <Stack flexDirection={'row'} gap={1} flexWrap="wrap">
                  {fields.length > 0
                    ? fields.map((source) => {
                        if (source.type === 'User Drive') {
                          return (
                            <AvatarChip
                              key={source.driveFolderOwner}
                              email={source.driveFolderOwner || ''}
                              type={'User Drive'}
                              onDelete={() => {
                                remove(fields.indexOf(source));
                              }}
                              size="small"
                              label={`${source.driveFolderOwner}: ${source.driveFolderName}`}
                            />
                          );
                        } else {
                          return (
                            <AvatarChip
                              key={source.value}
                              email={source.value}
                              type={
                                source.type as
                                  | 'User'
                                  | 'Group'
                                  | 'Domain'
                                  | 'OrgUnit'
                                  | 'Shared Drive'
                                  | 'RegEx'
                              }
                              onDelete={() => {
                                remove(fields.indexOf(source));
                              }}
                              size="small"
                              label={source.value}
                            />
                          );
                        }
                      })
                    : null}
                </Stack>
                {openDriveTreeDialog && userDriveAutoCompleteValue !== '' ? (
                  <DriveTreeDialog
                    open={openDriveTreeDialog}
                    handleClose={() => {
                      setOpenDriveTreeDialog(false);
                      setUserDriveAutoCompleteValue('');
                      autocompleteKey.current = new Date().getTime().toString();
                    }}
                    userEmail={userDriveAutoCompleteValue}
                    selectedFolder={selectedFolder}
                    setSelectedFolder={setSelectedFolder}
                    onSelection={() => {
                      addSelection({
                        type: 'User Drive',
                        value: userDriveAutoCompleteValue,
                        driveFolderOwner: userDriveAutoCompleteValue,
                        driveFolderId: selectedFolder,
                        driveFolderName: getDriveFolderName(
                          userDriveAutoCompleteValue,
                          selectedFolder
                        ),
                      });
                    }}
                  />
                ) : null}
              </Grid>
              {triggerGroup === 'drive' &&
                currentTriggerEventOption.type === 'drive' &&
                sourceType === 'User' && (
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <Stack gap={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={allSource}
                            onChange={(e) =>
                              handleAddAllUsersChecked(e.target.checked)
                            }
                          />
                        }
                        label="Add all users"
                      />
                      <Box>
                        <Typography>Users to Exclude</Typography>
                        <Stack flexDirection="row" gap={1} sx={{ mt: 1 }}>
                          <Controller
                            name={'targetsExcluded'}
                            control={control}
                            render={() => (
                              <Box sx={{ width: '100%' }}>
                                <UsersAutocomplete
                                  newKey={autocompleteExcludeKey.current}
                                  autocompleteValue={
                                    autoCompleteExcludeResponse
                                  }
                                  setAutoCompleteValue={(value) => {
                                    if (!value) return;
                                    const currentExcluded =
                                      getValues('targetsExcluded') ?? [];
                                    if (!currentExcluded.includes(value)) {
                                      setValue('targetsExcluded', [
                                        ...currentExcluded,
                                        value,
                                      ]);
                                    }
                                    autocompleteExcludeKey.current = `${new Date()
                                      .getTime()
                                      .toString()}-exclude`;
                                    setAutoCompleteExcludeResponse('');
                                  }}
                                  disabled={!allSource}
                                />
                              </Box>
                            )}
                          />
                        </Stack>
                        <Stack
                          flexDirection="row"
                          gap={1}
                          flexWrap="wrap"
                          sx={{ mt: 1, mb: 2 }}
                        >
                          {targetsExcluded?.map((user) => (
                            <AvatarChip
                              key={user}
                              label={user}
                              type={'User'}
                              email={user}
                              onDelete={() => handleRemoveExcludedUser(user)}
                            />
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  </Grid>
                )}
            </Grid>
          </Grid>
        </>
      )}
      {/* {currentTriggerEventOption.type === 'drive' ? (
        <Grid container size={{ xs: 10 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              width: '100%',
              mt: 2,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                bgcolor: theme.palette.primary.main,
                borderRadius: '50%',
              }}
            />
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Configuration
            </Typography>
          </Stack>
          <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
            <Typography variant="caption">
              Set a percentage threshold of drive usage to trigger a storage
              alert before reaching the maximum limit.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Controller
              name="triggerInfo.driveFileThreshold"
              control={control}
              rules={{
                min: { value: 0, message: 'must be numeric 0 - 100%.' },
                max: { value: 100, message: 'must be numeric 0 - 100%.' },
              }}
              render={({ field }) => (
                <TextField
                  autoFocus
                  label=""
                  inputProps={{
                    id: 'driveFileThreshold',
                    min: 0,
                    max: 100,
                  }}
                  InputLabelProps={{
                    htmlFor: 'driveFileThreshold',
                  }}
                  //onChange={(e) => setValue(e.target.value)}
                  type="number"
                  {...field}
                  sx={{ width: '100%' }}
                  autoComplete="off"
                  fullWidth
                  error={!!errors.triggerInfo?.driveFileThreshold}
                  helperText={
                    errors?.triggerInfo?.driveFileThreshold?.message || ' '
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      ) : null} */}
    </Stack>
  );
}
